import React, {useMemo} from "react";
import UpdatePaymentFeeButtonProperties from "./UpdatePaymentFeeButtonProperties";
import {useI18nContext} from "../../../contexts/I18nContext";
import UpdatePaymentFeeButtonStore from "./UpdatePaymentFeeButtonStore";
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import RoundedButton from "../../../ui_components/RoundedButton/RoundedButton";
import Modal from "../../../ui_components/Modal/Modal";
import {Observer} from "mobx-react-lite";
import {useStyles} from "./UpdatePaymentFeeButton.style";
import Button from "../../../ui_components/Button/Button";
import ButtonType from "../../../ui_components/Button/ButtonType";
import {useCommonContext} from "../../../contexts/CommonContext";
import ButtonSize from "../../../ui_components/Button/ButtonSize";
import {useUserContext} from "../../../contexts/UserContext";
import PaymentFee from "../../../entities/PaymentFee";
import Access from "../../Access/AllowAccess/Access";
import TabOption from "../../../ui_components/Tab/TabOption";
import ServiceName from "../../../entities/ServiceName";
import Tabs from "../../../ui_components/Tab/Tabs";
import PaymentFeeTab from "./PaymentFeeTab/PaymentFeeTab";

const UpdatePaymentFeeButton: React.FC<UpdatePaymentFeeButtonProperties> = ({
                                                                                onClick = () => {
                                                                                },
                                                                                onSave = () => {
                                                                                },
                                                                                showIcon = true,
                                                                                showText = true,
                                                                                disabled,
                                                                                className = "",
                                                                                agencyId,
                                                                                rounded = false,
                                                                                size = ButtonSize.DEFAULT,
                                                                                type = ButtonType.DEFAULT,
                                                                            }) => {
    const classes = useStyles(showText);

    const i18n = useI18nContext();
    const userStore = useUserContext();
    const commonContext = useCommonContext();
    const store = useMemo(() => new UpdatePaymentFeeButtonStore(commonContext, agencyId), [agencyId, commonContext]);

    const handleOnclick = async () => {
        await store.openModal();
        onClick();
    };

    const onCloseModal = () => {
        store.closeModal();
    };

    if (!userStore.hasAccess(Access.UPDATE_PAYMENT_FEES.valueOf())) {
        return <></>;
    }

    const basicText = i18n.translate("UPDATE_PAYMENT_FEES");
    const Btn = () => {
        if (rounded) {
            return (
                <RoundedButton
                    onClick={handleOnclick}
                    className={className}
                    disabled={disabled}
                    tooltip={!showText ? basicText : ""}
                    text={showText ? basicText : ""}
                    type={type}
                    icon={showIcon ? <RequestQuoteIcon/> : <></>}
                />
            );
        }
        return (
            <Button
                text={showText ? basicText : ""}
                className={className}
                tooltip={!showText ? basicText : ""}
                disabled={disabled}
                size={size}
                onClick={handleOnclick}
                type={type}
                startIcon={showIcon ? <RequestQuoteIcon/> : <></>}
            />
        );
    };

    const save = async () => {
        const success = await store.save();
        if (onSave) {
            onSave();
        }
        if (success) {
            store.closeModal();
        }
    }

    const update = (fees: PaymentFee[]) => {
        const map = new Map(
            fees.map(fee => [fee.id, fee])
        );

        store.fees.forEach(fee => {
            if (!map.has(fee.id)) {
                map.set(fee.id, fee);
            }
        });

        store.fees = Array.from(map.values());
    };

    const remove = (feeId: string) => {
        store.fees = store.fees.filter(f => f.id !== feeId);
    }

    return (
        <Observer>
            {() => {
                const tabsContent: TabOption[] = Object.values(ServiceName).map(
                    (service) => {
                        const serviceFees = store.fees
                            .filter(t => t.serviceName === service)
                            .sort((a, b) => a.paymentType.localeCompare(b.paymentType));
                        return new TabOption(
                            i18n.translate(service),
                            (
                                <PaymentFeeTab
                                    agencyId={''}
                                    serviceName={service}
                                    fees={serviceFees}
                                    onUpdate={update}
                                    onSave={save}
                                    onRemove={remove}
                                    loading={store.loading}
                                />
                            )
                        );
                    }
                );

                return (
                    <>
                        <Modal
                            fullScreen={false}
                            loading={store.loading}
                            title={basicText}
                            onClose={onCloseModal}
                            open={store.isModalOpen}
                        >
                            <div className={classes.modal_container}>
                                {!store.loading && store.fees.length > 0 && (
                                    <Tabs content={tabsContent} className={classes.tab_container_custom}/>
                                )}
                            </div>
                        </Modal>
                        <Btn/>
                    </>
                );
            }}
        </Observer>
    );
};

export default UpdatePaymentFeeButton;
